const ALPHA3_TO_ALPHA2: Record<string, string> = {
  HRV: 'HR',
  DEU: 'DE',
  AUT: 'AT',
  FRA: 'FR',
  ITA: 'IT',
  ESP: 'ES',
  PRT: 'PT',
  NLD: 'NL',
  BEL: 'BE',
  LUX: 'LU',
  CHE: 'CH',
  POL: 'PL',
  CZE: 'CZ',
  SVK: 'SK',
  SVN: 'SI',
  HUN: 'HU',
  ROU: 'RO',
  BGR: 'BG',
  GRC: 'GR',
  CYP: 'CY',
  MLT: 'MT',
  SWE: 'SE',
  DNK: 'DK',
  FIN: 'FI',
  NOR: 'NO',
  EST: 'EE',
  LVA: 'LV',
  LTU: 'LT',
  IRL: 'IE',
}

export function countryAlpha2(alpha3: string | null | undefined): string {
  if (!alpha3) return ''
  return ALPHA3_TO_ALPHA2[alpha3] ?? alpha3
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('hr-HR')
  } catch {
    return value
  }
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleDateString('hr-HR')
  } catch {
    return value
  }
}

// Renders a tender's value. `valueEur` (EUR-converted) is preferred when
// present; sources that only ever report a non-EUR currency (e.g. SIMAP,
// always CHF) never populate it, so we fall back to the source's original
// amount + currency rather than claiming no value is disclosed.
export function formatValue(
  valueEur: number | null | undefined,
  valueOriginal: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (valueEur !== null && valueEur !== undefined) {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(valueEur)
  }
  if (valueOriginal !== null && valueOriginal !== undefined && currency) {
    const amount = new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valueOriginal)
    return `${amount} ${currency}`
  }
  return 'Value not publicly disclosed'
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// AI score colouring: five evenly-spaced bands across 0–100, shared by the
// dashboard list and the procurement detail so the same percentage always
// renders in the same colour. 0% red → orange → yellow → blue → 100% green.
// Returns null when there's no score so the caller keeps the default colour.
export function aiScoreColor(score: number | null | undefined): string | null {
  if (score === null || score === undefined) return null
  if (score >= 80) return '#16a34a' // green
  if (score >= 60) return '#2563eb' // blue
  if (score >= 40) return '#ca8a04' // yellow
  if (score >= 20) return '#ea580c' // orange
  return '#dc2626' // red
}

export function formatDurationMs(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '—'
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 0 || Number.isNaN(ms)) return '—'
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s}s`
}
